/**
 * Formats a number with abbreviated suffixes (k, M, B, T) or scientific notation.
 *
 * For numbers below 1000: shows up to 3 decimal places but max 5 significant digits
 * For numbers >= 1000 and < 1e15: uses k/M/B/T suffixes with up to 2 decimal places
 * For numbers >= 1e15: shows scientific notation with 2 decimal places
 *
 * @example
 * formatNumber(0.12345)      // "0.123"
 * formatNumber(4.12345)      // "4.123"
 * formatNumber(25.12345)     // "25.123"
 * formatNumber(100.12345)    // "100.12"
 * formatNumber(999.12345)    // "999.12"
 * formatNumber(1000)         // "1k"
 * formatNumber(10200)        // "10.2k"
 * formatNumber(1000000)      // "1M"
 * formatNumber(1e15)         // "1.00E+15"
 */
export const formatNumber = (
  value: number | string | null | undefined,
  precision = 2
): string => {
  if (value == null || value === '') return '';

  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  const abs = Math.abs(number);
  const sign = number < 0 ? '-' : '';

  // For numbers below 1000: up to 3 decimals, max 5 significant digits
  if (abs < 1e3) {
    const integerDigits = abs < 1 ? 1 : Math.floor(abs).toString().length;
    const maxDecimals = Math.max(0, Math.min(3, 5 - integerDigits));
    return parseFloat(number.toFixed(maxDecimals)).toString();
  }

  // For numbers >= 1000: use k/M/B/T suffixes, remove trailing zeros with +
  // Handle rounding up to next unit (e.g., 999999 → 1M instead of 1000k)
  const format = (val: number, divisor: number, suffix: string, nextSuffix: string) => {
    const result = +(val / divisor).toFixed(precision);
    if (result >= 1000) return `${sign}1${nextSuffix}`;
    return `${sign}${result}${suffix}`;
  };

  if (abs < 1e6) return format(abs, 1e3, 'k', 'M');
  if (abs < 1e9) return format(abs, 1e6, 'M', 'B');
  if (abs < 1e12) return format(abs, 1e9, 'B', 'T');
  if (abs < 1e15) return format(abs, 1e12, 'T', 'E+15');

  // For numbers >= 1e15: scientific notation
  return number.toExponential(2).toUpperCase();
};

/**
 * Formats a number for Y-axis labels in charts.
 * Same logic as formatNumber but returns a string and optionally adds percentage suffix.
 */
export const formatNumberToString = (
  value: number | string | null | undefined,
  isPercentage = false
): string => {
  const formatted = formatNumber(value);
  const result = String(formatted);
  if (result === '') return '';
  return isPercentage ? `${result}%` : result;
};

export default formatNumber;
